import { Autocomplete } from '@ovComponents/2-component/autocomplete/autocomplete';
import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import { Box, Typography, useTheme } from 'hephaestus/web';
import type { ChangeEvent, SyntheticEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PortfolioDownloadButton } from '../components/portfolioDownloadButton';
import {
  COMPACT_SEARCH_AUTOCOMPLETE_SX,
  COMPACT_SELECT_FIELD_SX,
  SEARCH_AUTOCOMPLETE_POPPER_SX,
} from '../styles';
import { getSearchPlaceholder } from '../utils/portfolioFilters';
import {
  type PortfolioSearchOption,
  TOOLBAR_SEARCH_MIN_CHARS,
} from '../utils/portfolioSearchOptions';
import { ModelFilterSelect } from './modelFilterSelect';
import {
  STATUS_FILTER_OPTIONS,
  TOOLBAR_SEARCH_DEBOUNCE_MS,
  TYPE_FILTER_OPTIONS,
} from './portfolioFilters.config';
import { MODEL_FILTER_FIELD_HEIGHT } from './portfolioFilters.styles';
import type { PortfolioFiltersToolbarProps } from './portfolioFiltersToolbar.types';

export type { PortfolioFiltersToolbarProps } from './portfolioFiltersToolbar.types';

export const PortfolioFiltersToolbar = ({
  viewMode,
  searchTerm,
  onSearchChange,
  onSearchOptions,
  onSearchOptionsError,
  statusFilter,
  onStatusChange,
  portfolioTypeFilter,
  onPortfolioTypeChange,
  modelFilter,
  onModelChange,
  modelOptions,
  onSearchModels,
  modelOptionsLoading = false,
  hasActiveFilters,
  onClearFilters,
  organizationId,
  exportFilter,
  canExport = false,
}: PortfolioFiltersToolbarProps) => {
  const theme = useTheme();
  const { t } = useTranslation('portfolioTradingHub');
  const isDownloadReady =
    canExport && organizationId && exportFilter?.organizationId
      ? { organizationId, exportFilter }
      : null;

  const previousSearchTermRef = useRef(searchTerm);
  const [clearCount, setClearCount] = useState(0);
  useEffect(() => {
    if (previousSearchTermRef.current !== '' && searchTerm === '') {
      setClearCount((count) => count + 1);
    }
    previousSearchTermRef.current = searchTerm;
  }, [searchTerm]);
  const searchAutocompleteKey = `portfolio-search-${clearCount}`;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1,
        py: 0.5,
        borderBottom: '1px solid',
        borderColor: theme.color.outlineVariant,
        bgcolor: theme.color.background,
      }}
    >
      <Box sx={{ width: 200, flexShrink: 0, overflow: 'hidden' }}>
        <Autocomplete
          key={searchAutocompleteKey}
          testId='portfolio-filters-search'
          label=''
          placeholder={t(`filters.${getSearchPlaceholder(viewMode)}`)}
          value={searchTerm || null}
          freeSolo
          onChange={(
            _event: SyntheticEvent,
            next: PortfolioSearchOption | string | null,
          ) => {
            if (next == null) {
              onSearchChange('');
              return;
            }
            onSearchChange(
              typeof next === 'string'
                ? next
                : (next.searchValue ?? next.label),
            );
          }}
          onInputChange={(
            _event: SyntheticEvent,
            value: string,
            reason: string,
          ) => {
            if (reason === 'reset') return;
            if (reason === 'input' || reason === 'clear') {
              onSearchChange(value);
            }
          }}
          onSearch={onSearchOptions}
          onSearchError={onSearchOptionsError}
          debounceMs={TOOLBAR_SEARCH_DEBOUNCE_MS}
          minChars={TOOLBAR_SEARCH_MIN_CHARS}
          searchOnOpen={false}
          showSearchIcon
          fullWidth
          size='small'
          getOptionLabel={(option: PortfolioSearchOption | string) =>
            typeof option === 'string' ? option : option.label
          }
          isOptionEqualToValue={(
            a: PortfolioSearchOption,
            b: PortfolioSearchOption,
          ) => a.id === b.id}
          sx={COMPACT_SEARCH_AUTOCOMPLETE_SX}
          componentsProps={{ popper: { sx: SEARCH_AUTOCOMPLETE_POPPER_SX } }}
        />
      </Box>

      {viewMode === 'portfolios' && (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <Box sx={{ minWidth: 130 }}>
              <SelectField
                label=''
                value={statusFilter}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  onStatusChange(e.target.value)
                }
                size='small'
                sx={COMPACT_SELECT_FIELD_SX}
                testId='portfolio-filters-status'
              >
                {STATUS_FILTER_OPTIONS.map(({ value, labelKey }) => (
                  <MenuItem key={value} value={value}>
                    {t(`filters.${labelKey}`)}
                  </MenuItem>
                ))}
              </SelectField>
            </Box>
            <Box sx={{ minWidth: 110 }}>
              <SelectField
                label=''
                value={portfolioTypeFilter}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  onPortfolioTypeChange(e.target.value)
                }
                size='small'
                sx={COMPACT_SELECT_FIELD_SX}
                testId='portfolio-filters-type'
              >
                {TYPE_FILTER_OPTIONS.map(({ value, labelKey }) => (
                  <MenuItem key={value} value={value}>
                    {t(`filters.${labelKey}`)}
                  </MenuItem>
                ))}
              </SelectField>
            </Box>
            <Box
              sx={{
                minWidth: 180,
                flexShrink: 0,
                height: MODEL_FILTER_FIELD_HEIGHT,
                maxHeight: MODEL_FILTER_FIELD_HEIGHT,
                overflow: 'hidden',
              }}
            >
              <ModelFilterSelect
                value={modelFilter}
                preloadedModelOptions={modelOptions}
                onSearchModels={onSearchModels}
                onChange={onModelChange}
                loading={modelOptionsLoading}
                placeholder={t('filters.allModels')}
              />
            </Box>

            {hasActiveFilters && (
              <Typography
                variant='labelSmall'
                onClick={onClearFilters}
                sx={{
                  color: theme.color.primary,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  '&:hover': { textDecoration: 'underline' },
                }}
                data-testid='portfolio-filters-clear'
              >
                {t('filters.clear')}
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              flex: 1,
            }}
          />

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            {isDownloadReady && (
              <PortfolioDownloadButton
                organizationId={isDownloadReady.organizationId}
                listFilter={isDownloadReady.exportFilter}
              />
            )}
          </Box>
        </>
      )}
    </Box>
  );
};
